import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { FaIdCard, FaSpinner } from 'react-icons/fa';
import { cartoesAPI } from '../services/api';
import { downloadBlob } from '../utils/downloadUtils';

// --- Styled Components ---
const CardContainer = styled(motion.div)`
 background: ${props => props.theme.colors.white};
 border-radius: 16px;
 padding: 24px;
 box-shadow: ${props => props.theme.shadows.card};
 border: 1px solid ${props => props.theme.colors.border};
 margin-top: 24px;
`;

const CardHeader = styled.div`
 display: flex;
 justify-content: space-between;
 align-items: center;
 margin-bottom: 20px;
 padding-bottom: 16px;
 border-bottom: 2px solid ${props => props.theme.colors.borderLight};
`;

const CardTitle = styled.h3`
 color: ${props => props.theme.colors.primary};
 font-size: 18px;
 font-weight: 600;
 display: flex;
 align-items: center;
 gap: 8px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
`;

const Description = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 15px;
  max-width: 400px;
`;

const GenerateButton = styled.button`
 background: ${props => props.theme.colors.secondary};
 color: ${props => props.theme.colors.white};
 border: none;
 padding: 12px 24px;
 border-radius: 8px;
 font-size: 15px;
 font-weight: 600;
 cursor: pointer;
 transition: all 0.3s ease;
 display: flex;
 align-items: center;
 gap: 8px;
 
 &:hover:not(:disabled) {
  background: ${props => props.theme.colors.primary};
  transform: translateY(-2px);
 }
 
 &:disabled {
  opacity: 0.7;
  cursor: not-allowed;
 }

 @keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
 }

 .spinner {
  animation: spin 1s linear infinite;
 }
`;


const CardGenerator = ({ membros, contrato }) => {
    const [isGenerating, setIsGenerating] = useState(false);

    const handleGenerateCard = async () => {
        if (!membros || membros.length === 0 || !contrato) {
            toast.error("Dados do titular ou contrato não encontrados.");
            return;
        }

        setIsGenerating(true);
        toast.info(`Gerando ${membros.length} cartão(ões), por favor aguarde...`);

        try {
            const response = await cartoesAPI.gerar(membros, contrato);
            
            // Criar blob do PDF
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const filename = membros.length === 1
                ? `cartao-${membros[0].nome.replace(/\s+/g, '_')}-${contrato}.pdf`
                : `cartoes-contrato-${contrato}.pdf`;
            
            // Usar função utilitária compatível com iOS
            await downloadBlob(blob, filename);
            
            toast.success("Todos os cartões foram gerados com sucesso!");

        } catch (error) {
            console.error("Erro ao gerar PDF:", error);
            const errorMessage = error.response?.data?.message || error.message || "Erro desconhecido";
            toast.error(`Ocorreu um erro ao gerar os cartões: ${errorMessage}`);
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <>
            <CardContainer>
                <CardHeader>
                    <CardTitle><FaIdCard /> Cartão de Associados</CardTitle>
                </CardHeader>
                <ContentWrapper>
                    <Description>
                        Gere uma versão para impressão (A4) do cartão para o titular e todos os dependentes.
                    </Description>
                    <GenerateButton onClick={handleGenerateCard} disabled={isGenerating}>
                        {isGenerating ? (
                            <> <FaSpinner className="spinner" /> Gerando... </>
                        ) : ('Gerar Todos os Cartões (A4)')}
                    </GenerateButton>
                </ContentWrapper>
            </CardContainer>

        </>
    );
};

export default CardGenerator;
